import { Select, TextField } from "@king-ict/eupisi-ui/components";
import type { CodeBook } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import OtherData from "../../../../components/OtherData";
import type { ProgramCodeBook } from "../../../../models/Program";
import {
  OtherEnrollmentConditionId,
  OtherFormOfTeachingId,
  OtherKnowledgeCheckId,
  type Module,
} from "../../../../models/ProgramRequest";
import { ModuleInfo } from "../../../ProgramRequestEdit";
import styles from "../../RequestCodeBook.module.scss";
import { getTotalProgramDuration } from "../utils";

type Props = {
  programName: ProgramCodeBook;
  setProgramName: React.Dispatch<React.SetStateAction<ProgramCodeBook>>;
  readonly: boolean;
  getErrorForPath: (path: string) => string | undefined;
  formsOfTeaching?: CodeBook[];
  knowledgeChecks?: CodeBook[];
  enrollmentConditions?: CodeBook[];
  onMinisterMultiSelectChange: (
    ids: number[],
    otherId: number,
    idsField: "formOfTeachingIds" | "knowledgeCheckIds" | "enrollmentConditionIds",
    otherField: "otherFormsOfTeaching" | "otherKnowledgeChecks" | "otherEnrollmentConditions",
  ) => void;
  handleChange: (setState: (previousState: any) => void) => (event: any) => void;
};

export const ProgramNameMinisterFieldsEdit = ({
  programName,
  setProgramName: _setProgramName,
  readonly,
  getErrorForPath,
  formsOfTeaching,
  knowledgeChecks,
  enrollmentConditions,
  onMinisterMultiSelectChange,
  handleChange,
}: Props) => {
  const { t } = useTranslation();

  const detail = programName.ministerDetail;
  const totalDuration = getTotalProgramDuration(programName);
  const durationError = getErrorForPath("durationChosen");
  const modulesError = getErrorForPath("ministerDetail.modules");
  const competencesError = getErrorForPath("ministerDetail.competences");
  const getMinisterDetailErrorForPath = (path: string) => getErrorForPath(`ministerDetail.${path}`);

  return (
    <>
      <Row className={styles.dataRow}>
        <Col md={3}>
          <TextField label={t("common.programDuration")} type="number" value={totalDuration} readonly plaintext />
        </Col>
        <Col md={3}>
          <TextField
            label={t("request.guidedLearningDuration")}
            name="ministerDetail.guidedLearningDuration"
            type="number"
            value={detail?.guidedLearningDuration ?? 0}
            onChange={handleChange(_setProgramName)}
            min={0}
            maxLength={5}
            errorText={getErrorForPath("ministerDetail.guidedLearningDuration") ?? durationError}
            readonly={readonly}
            plaintext={readonly}
          />
        </Col>
        <Col md={3}>
          <TextField
            label={t("request.workLearningDuration")}
            name="ministerDetail.workLearningDuration"
            type="number"
            value={detail?.workLearningDuration ?? 0}
            onChange={handleChange(_setProgramName)}
            min={0}
            maxLength={5}
            errorText={getErrorForPath("ministerDetail.workLearningDuration") ?? durationError}
            readonly={readonly}
            plaintext={readonly}
          />
        </Col>
        <Col md={3}>
          <TextField
            label={t("request.independentLearningDuration")}
            name="ministerDetail.independentLearningDuration"
            type="number"
            value={detail?.independentLearningDuration ?? 0}
            onChange={handleChange(_setProgramName)}
            min={0}
            maxLength={5}
            errorText={getErrorForPath("ministerDetail.independentLearningDuration") ?? durationError}
            readonly={readonly}
            plaintext={readonly}
          />
        </Col>
      </Row>

      <Row className={styles.dataRow}>
        <Col md={4}>
          <Select
            multiple
            label={t("request.formOfTeaching")}
            name="ministerDetail.formOfTeachingIds"
            value={detail?.formOfTeachingIds ?? []}
            onChange={(e: any) =>
              onMinisterMultiSelectChange(
                e.target.value as number[],
                OtherFormOfTeachingId,
                "formOfTeachingIds",
                "otherFormsOfTeaching",
              )
            }
            selectData={codeBookToSelectData(formsOfTeaching)}
            errorText={getErrorForPath("ministerDetail.formOfTeachingIds")}
            disabled={readonly}
          />
        </Col>
        <Col md={4}>
          <Select
            multiple
            label={t("request.knowledgeChecks")}
            name="ministerDetail.knowledgeCheckIds"
            value={detail?.knowledgeCheckIds ?? []}
            onChange={(e: any) =>
              onMinisterMultiSelectChange(
                e.target.value as number[],
                OtherKnowledgeCheckId,
                "knowledgeCheckIds",
                "otherKnowledgeChecks",
              )
            }
            selectData={codeBookToSelectData(knowledgeChecks)}
            errorText={getErrorForPath("ministerDetail.knowledgeCheckIds")}
            disabled={readonly}
          />
        </Col>
        <Col md={4}>
          <Select
            multiple
            label={t("request.enrollmentConditions")}
            name="ministerDetail.enrollmentConditionIds"
            value={detail?.enrollmentConditionIds ?? []}
            onChange={(e: any) =>
              onMinisterMultiSelectChange(
                e.target.value as number[],
                OtherEnrollmentConditionId,
                "enrollmentConditionIds",
                "otherEnrollmentConditions",
              )
            }
            selectData={codeBookToSelectData(enrollmentConditions)}
            errorText={getErrorForPath("ministerDetail.enrollmentConditionIds")}
            disabled={readonly}
          />
        </Col>
      </Row>

      {(detail?.formOfTeachingIds ?? []).includes(OtherFormOfTeachingId) && (
        <OtherData
          name="ministerDetail.otherFormsOfTeaching"
          header={t("request.formOfTeaching")}
          label={t("request.otherFormsOfTeaching")}
          addButtonLabel={t("request.addNewFormOfTeaching")}
          values={detail?.otherFormsOfTeaching ?? []}
          onChange={handleChange(_setProgramName)}
          getErrorForPath={getErrorForPath}
          readonly={readonly}
        />
      )}

      {(detail?.knowledgeCheckIds ?? []).includes(OtherKnowledgeCheckId) && (
        <OtherData
          name="ministerDetail.otherKnowledgeChecks"
          header={t("request.knowledgeChecks")}
          label={t("request.otherKnowledgeChecks")}
          addButtonLabel={t("request.addNewKnowledgeCheck")}
          values={detail?.otherKnowledgeChecks ?? []}
          onChange={handleChange(_setProgramName)}
          getErrorForPath={getErrorForPath}
          readonly={readonly}
        />
      )}

      {(detail?.enrollmentConditionIds ?? []).includes(OtherEnrollmentConditionId) && (
        <OtherData
          name="ministerDetail.otherEnrollmentConditions"
          header={t("request.enrollmentConditions")}
          label={t("request.otherEnrollmentConditions")}
          addButtonLabel={t("request.addNewEnrollmentCondition")}
          values={detail?.otherEnrollmentConditions ?? []}
          onChange={handleChange(_setProgramName)}
          getErrorForPath={getErrorForPath}
          readonly={readonly}
        />
      )}

      <OtherData
        name="ministerDetail.competences"
        header={t("request.competences")}
        label={t("request.competenceName")}
        addButtonLabel={t("request.competenceAdd")}
        values={detail?.competences ?? [""]}
        onChange={handleChange(_setProgramName)}
        getErrorForPath={getErrorForPath}
        readonly={readonly}
      />
      {!readonly && competencesError && <div className="text-danger mt-2">{competencesError}</div>}

      <div className="mt-4">
        <ModuleInfo
          name="ministerDetail.modules"
          modules={(detail?.modules as unknown as Module[]) ?? []}
          qualificationType={programName.qualificationTypeId ?? 0}
          getErrorForPath={getMinisterDetailErrorForPath}
          onChange={handleChange(_setProgramName)}
        />
        {!readonly && modulesError && <div className="text-danger mt-2">{modulesError}</div>}
      </div>
    </>
  );
};

